import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

/*
 * The 6-digit code a sign-up is mailed (routes/email_codes.py, migration
 * 0107), typed with the password chosen at sign-up -- which is what stops
 * someone who registered another person's address from having that person's
 * code activate it (POST /auth/verify-email-code).
 *
 * Six boxes; a pasted code fills them all. The server answers every failure
 * alike (invalid_code), five tries per code; a new code (Send a new code,
 * once a minute) replaces the old one. Typed right, a public guest the code
 * was meant to let in gets a session -- `onSignedIn(token)` -- and anyone
 * else is told a manager will review the account.
 */
const LENGTH = 6;
const RESEND_SECONDS = 60;

export function waitMinutes(data) {
  return Math.max(1, Math.ceil((Number(data?.retry_after) || 60) / 60));
}

export default function OtpEntry({ email, instantAccess = false, justSent = true, onSignedIn, onBack, backLabel }) {
  const { t } = useTranslation('register');
  const [digits, setDigits] = useState(Array(LENGTH).fill(''));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);   // the account type, once verified and waiting
  // A code just sent: a new one is a minute away (the server's own throttle).
  const [wait, setWait] = useState(justSent ? RESEND_SECONDS : 0);
  const [resent, setResent] = useState('');
  const boxes = useRef([]);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setWait(w => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const fill = (from, text) => {
    const found = String(text || '').replace(/\D/g, '').slice(0, LENGTH - from).split('');
    if (!found.length) return;
    setDigits(d => d.map((v, i) => (i >= from && i < from + found.length ? found[i - from] : v)));
    boxes.current[Math.min(from + found.length, LENGTH - 1)]?.focus();
  };

  const onKeyDown = (i) => (e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) boxes.current[i - 1]?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const code = digits.join('');
    if (code.length !== LENGTH) { setError(t('code.incomplete')); return; }
    if (!password) { setError(t('code.passwordRequired')); return; }
    setBusy(true);
    try {
      const res = await api.post('/auth/verify-email-code', { email, code, password });
      if (res.data?.token) onSignedIn?.(res.data.token);
      else setPending(res.data?.account_type || 'staff');
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 429) setError(t('errors.rate_limited', { minutes: waitMinutes(data) }));
      else if (data?.code === 'invalid_code') setError(t('code.invalid'));
      else setError(t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResent('');
    setError('');
    try {
      await api.post('/auth/resend-verification', { email });
      setDigits(Array(LENGTH).fill(''));
      setResent(t('code.resent'));
      setWait(RESEND_SECONDS);
    } catch (err) {
      if (err.response?.status === 429) setError(t('errors.rate_limited', { minutes: waitMinutes(err.response.data) }));
      else setError(t('errors.verification_email_failed'));
    }
  };

  if (pending) {
    return (
      <div data-testid="code-pending" role="status">
        <h2 style={styles.heading}>{t('code.pendingTitle')}</h2>
        <p style={styles.text}>{t(pending === 'public' ? 'code.pendingPublic' : 'code.pendingStaff')}</p>
        {onBack && (
          <button type="button" onClick={onBack} title={t('tip.backToSignIn')} style={styles.linkBtn}>
            {backLabel || t('backToSignIn')}
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="code-entry">
      <h2 style={styles.heading}>{t('code.title')}</h2>
      <p style={styles.text}>{t('code.sentTo', { email })}</p>
      <p style={styles.note}>{t(instantAccess ? 'code.nextInstant' : 'code.nextApproval')}</p>

      <div role="group" aria-label={t('code.label')} style={styles.row} dir="ltr">
        {digits.map((d, i) => (
          <input key={i} ref={el => { boxes.current[i] = el; }} value={d} inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={LENGTH}
            aria-label={t('code.digit', { n: i + 1, of: LENGTH })} data-testid={`code-digit-${i}`}
            onKeyDown={onKeyDown(i)}
            onPaste={(e) => { e.preventDefault(); fill(i, e.clipboardData.getData('text')); }}
            onChange={(e) => {
              const typed = e.target.value.replace(/\D/g, '');
              if (typed.length > 1) { fill(i, typed); return; }
              setDigits(ds => ds.map((v, j) => (j === i ? typed : v)));
              if (typed && i < LENGTH - 1) boxes.current[i + 1]?.focus();
            }}
            style={styles.digit} />
        ))}
      </div>

      <label htmlFor="code-password" style={styles.label}>{t('code.password')}</label>
      <input id="code-password" type="password" value={password} autoComplete="current-password"
        onChange={e => setPassword(e.target.value)} title={t('tip.codePassword')} style={styles.input}
        data-testid="code-password" />

      {error && <div role="alert" style={styles.errorBox}>{error}</div>}
      {resent && <p role="status" style={styles.ok}>{resent}</p>}

      <button type="submit" disabled={busy} title={t('tip.codeVerify')} style={styles.primaryBtn(busy)}
        data-testid="code-verify">
        {busy ? t('code.verifying') : t('code.verify')}
      </button>
      <button type="button" onClick={resend} disabled={wait > 0} title={t('tip.codeResend')} style={styles.linkBtn}
        data-testid="code-resend">
        {wait > 0 ? t('code.resendIn', { seconds: wait }) : t('code.resend')}
      </button>
      {onBack && (
        <button type="button" onClick={onBack} title={t('tip.backToSignIn')}
          style={{ ...styles.linkBtn, marginInlineStart: 16 }}>
          {backLabel || t('backToSignIn')}
        </button>
      )}
    </form>
  );
}

const styles = {
  heading: { margin: '0 0 12px', fontSize: 17, color: 'var(--c-primary-fg)' },
  text: { margin: '0 0 8px', fontSize: 14, color: 'var(--c-text-2-fg)', lineHeight: 1.5 },
  note: { margin: '0 0 12px', fontSize: 13, color: 'var(--c-text-body-3-fg)', lineHeight: 1.5 },
  row: { display: 'flex', gap: 8, justifyContent: 'center', margin: '8px 0 16px' },
  digit: {
    width: 40, height: 48, textAlign: 'center', fontSize: 22, fontVariantNumeric: 'tabular-nums',
    border: '1px solid var(--c-border-strong)', borderRadius: 8, boxSizing: 'border-box',
  },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--c-text-body-fg)', marginBottom: 4 },
  input: {
    display: 'block', width: '100%', padding: '10px 12px', marginBottom: 12,
    border: '1px solid var(--c-border-strong)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
  },
  errorBox: {
    background: 'var(--c-danger-soft)', color: 'var(--c-danger-2-fg)', borderRadius: 8,
    padding: '10px 14px', fontSize: 13, marginBottom: 12,
  },
  ok: { margin: '0 0 12px', fontSize: 13, color: 'var(--c-success-3-fg)' },
  primaryBtn: busy => ({
    display: 'block', width: '100%', padding: 11, background: busy ? 'var(--c-primary-3)' : 'var(--c-primary)',
    color: 'var(--c-on-accent)', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
    cursor: busy ? 'not-allowed' : 'pointer',
  }),
  linkBtn: {
    marginTop: 12, background: 'none', border: 'none', padding: 0, color: 'var(--c-primary-fg)',
    textDecoration: 'underline', cursor: 'pointer', fontSize: 13,
  },
};
